import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import HashMap from 'src/app/types/hash-map';
import { CommitteeApproval } from './committee-approval';
import queries from './committee-approval/graphql/committee-approval';

export type CommitteeApprovalResult = {
  approvals: CommitteeApproval[];
  isLoading: boolean;
};
type CommitteeApprovalMap = {
  result: CommitteeApprovalResult;
  approvalsBehavior: BehaviorSubject<CommitteeApprovalResult>;
};
type MilestoneCommitteeApprovalHash = HashMap<CommitteeApprovalMap>;

type ProjectCommitteeApprovalHash = HashMap<MilestoneCommitteeApprovalHash>;

type ProjectQueryStore = {
  query: QueryRef<any>;
  sub: Subscription;
  loading: boolean;
};
@Injectable({
  providedIn: 'root',
})
export class CommitteeApprovalsService {
  /**
   * Object with arrays of CommitteeApprovals organized by Project and Milestone.
   */
  private _committeeApprovals: ProjectCommitteeApprovalHash = {};
  private _projectQueries: HashMap<ProjectQueryStore> = {};
  constructor(public apollo: Apollo) {}

  /**
   * Gets the observable for the given project and milestone's approvals. Will also request
   *
   * @param project_id ID of project for approvals
   * @param milestone_id ID of milestone for approvals
   * @return Observable<CommitteeApprovalResult>
   */
  public getApprovals(
    project_id: string,
    milestone_id: string
  ): Observable<CommitteeApprovalResult> {
    this._instantiateProject(project_id, milestone_id);
    return this._committeeApprovals[project_id][
      milestone_id
    ].approvalsBehavior.asObservable();
  }
  /**
   * Receive a complete list of approvals from whatever source. If it is passed an unsorted array of approvals, it will sort them into the appropriate milestones
   *
   * @param project_id ID of Project for approvals
   * @param milestone_id ID of milestone for approvals
   * @param approvals array of approvals
   */
  public receiveCommitteeApprovals(
    project_id: string,
    approvals: CommitteeApproval[],
    milestone_id?: string
  ) {
    if (milestone_id) {
      this._instantiateProject(project_id, milestone_id);
      this._committeeApprovals[project_id][milestone_id].result = {
        approvals: [...approvals],
        isLoading: false,
      };
      this._committeeApprovals[project_id][milestone_id].approvalsBehavior.next(
        this._committeeApprovals[project_id][milestone_id].result
      );
    } else {
      let milestones: { [key: string]: CommitteeApproval[] } = {};
      if (approvals) {
        for (let i = 0; i < approvals.length; i++) {
          let committeeApproval: CommitteeApproval = approvals[i];
          if (committeeApproval.milestone_id) {
            if (!milestones[committeeApproval.milestone_id]) {
              milestones[committeeApproval.milestone_id] = [];
            }
            milestones[committeeApproval.milestone_id].push({
              ...committeeApproval,
            });
          }
        }
        let milestone_keys = Object.keys(milestones);
        for (let i = 0; i < milestone_keys.length; i++) {
          let key = milestone_keys[i];
          this.receiveCommitteeApprovals(project_id, milestones[key], key);
        }
      }
    }
  }
  /**
   * Sets the loading status for a project and milestone's approvals
   *
   * @param project_id ID of project to set loading for
   * @param milestone_id ID of milestone to set loading for
   * @param isLoading new loading status
   */
  private _setLoading(
    project_id: string,
    milestone_id: string,
    isLoading: boolean
  ) {
    this._committeeApprovals[project_id][milestone_id].result.isLoading =
      isLoading;
    this._committeeApprovals[project_id][milestone_id].approvalsBehavior.next(
      this._committeeApprovals[project_id][milestone_id].result
    );
  }
  public isLoading(project_id: string): boolean {
    return (
      this._projectQueries[project_id] &&
      this._projectQueries[project_id].loading
    );
  }
  /**
   * Request a fresh set of approvals. If milestone is not provided, all milestones will be fetched and their approvals sorted.
   *
   * @param project_id ID of project
   * @param milestone_id ID of milestone (optional)
   */
  public requestApprovals(project_id: string, milestone_id?: string) {
    if (!this._projectQueries[project_id]) {
      let data: any = {
        input: { project_id },
      };
      let query: QueryRef<any> = this.apollo.watchQuery<any>({
        query: queries.REQUEST_APPROVALS,
        variables: data,
      });
      let sub: Subscription = query.valueChanges.subscribe(
        ({ data, loading }) => {
          if (data.committeeApprovals) {
            this.receiveCommitteeApprovals(
              project_id,
              data.committeeApprovals,
              milestone_id
            );
          }
          if (milestone_id) {
            sub.unsubscribe();
          } else if (!this._projectQueries[project_id]) {
            this._persistQueries(project_id, query, sub, loading);
          }
        }
      );
    } else {
      this._projectQueries[project_id].query.refetch();
    }
  }
  private _persistQueries(
    project_id: string,
    query: QueryRef<any>,
    sub: Subscription,
    loading: boolean
  ) {
    let queryStore: ProjectQueryStore = {
      sub,
      query,
      loading,
    };
    this._projectQueries[project_id] = queryStore;
  }
  public cleanUp(project_id?: string) {
    let keys = Object.keys(this._projectQueries);
    if (!project_id) {
      for (let i = 0; i < keys.length; i++) {
        this.cleanUp(keys[i]);
      }
    } else {
      let { query, sub } = this._projectQueries[project_id];
      query.stopPolling();
      sub.unsubscribe();
    }
  }
  /**
   * Creates the milestone key for the given milestone within the Project provided.
   *
   * @param project_id ID of project for approvals
   * @param milestone_id ID of milestone for approvals
   */
  private _instantiateMilestone(project_id: string, milestone_id: string) {
    if (!this._committeeApprovals[project_id][milestone_id]) {
      let approvals: CommitteeApproval[] = [];
      let result: CommitteeApprovalResult = {
        approvals,
        isLoading: false,
      };
      let approvalsBehavior = new BehaviorSubject<CommitteeApprovalResult>(
        result
      );
      this._committeeApprovals[project_id][milestone_id] = {
        result,
        approvalsBehavior,
      };
    }
  }
  /**
   * Creates the Project key for the given Project, and optionally calls the milestone instantiator if the milestone_id is called.
   *
   * @param project_id ID of Project for approvals
   * @param milestone_id ID of Milestone for approvals (optional)
   */
  private _instantiateProject(project_id: string, milestone_id: string | null) {
    if (!this._committeeApprovals[project_id]) {
      this._committeeApprovals[project_id] = {};
    }
    if (milestone_id) {
      this._instantiateMilestone(project_id, milestone_id);
    }
  }
  private _mutateApproval(
    project_id: string,
    milestone_id: string,
    approval: CommitteeApproval
  ) {}
}
